import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Modal from '../../components/UI/Modal';
import { contactAPI } from '../../services/api';
import { 
  Mail, Eye, Trash2, Archive, CheckCircle, MessageSquare, Clock, Search, X
} from 'lucide-react';

const MessagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled(Card)`
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StatIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.color}20;
  color: ${props => props.color};
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatLabel = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 0.25rem;
`;

const StatValue = styled.div`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
`;

const Controls = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 2rem;
`;

const SearchBox = styled.div`
  flex: 1;
  min-width: 250px;
  position: relative;
  
  input {
    width: 100%;
    padding: 0.75rem 2.5rem 0.75rem 1rem;
    border: 1px solid ${props => props.theme.colors.border};
    border-radius: ${props => props.theme.borderRadius.md};
    font-size: ${props => props.theme.typography.fontSize.base};
    
    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary};
    }
  }
  
  svg {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: ${props => props.theme.colors.text.secondary};
  }
`;

const FilterButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 0.75rem 1.25rem;
  border: 1px solid ${props => props.active ? props.theme.colors.primary : props.theme.colors.border};
  background: ${props => props.active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.colors.text.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? props.theme.colors.primaryDark : props.theme.colors.primary}15;
  }
`;

const MessagesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.sm};
`;

const TableHead = styled.thead`
  background: ${props => props.theme.colors.primary}10;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${props => props.theme.colors.border};
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: ${props => props.theme.colors.background.secondary};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableHeader = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const TableCell = styled.td`
  padding: 1rem;
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  text-transform: uppercase;
  
  ${props => {
    switch (props.status) {
      case 'new':
        return `
          background: #e3f2fd;
          color: #1976d2;
        `;
      case 'read':
        return `
          background: #fff3e0;
          color: #f57c00;
        `;
      case 'replied':
        return `
          background: #e8f5e9;
          color: #388e3c;
        `;
      case 'archived':
        return `
          background: #f5f5f5;
          color: #757575;
        `;
      default:
        return '';
    }
  }}
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const MessageDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const DetailRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DetailLabel = styled.div`
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const DetailValue = styled.div`
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.6;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: ${props => props.theme.typography.fontSize.base};
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ContactMessages = () => {
  const { theme } = useTheme();
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchMessages();
    fetchStats();
  }, [statusFilter]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      const response = await contactAPI.getMessages(params);
      setMessages(response.data.data || response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await contactAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMessages();
  };

  const viewMessage = async (message) => {
    try {
      const response = await contactAPI.getMessage(message.id);
      setSelectedMessage(response.data);
      setAdminNotes(response.data.admin_notes || '');
      setShowDetailModal(true);
      
      // Refresh messages to update status
      fetchMessages();
      fetchStats();
    } catch (error) {
      console.error('Error fetching message:', error);
    }
  };

  const updateMessageStatus = async (status) => {
    try {
      await contactAPI.updateMessage(selectedMessage.id, { status, admin_notes: adminNotes });
      setShowDetailModal(false);
      fetchMessages();
      fetchStats();
      alert('Message status updated successfully!');
    } catch (error) {
      console.error('Error updating message:', error);
      alert('Failed to update message status');
    }
  };

  const deleteMessage = async (messageId) => {
    if (window.confirm('Are you sure you want to delete this message? This action cannot be undone.')) {
      try {
        await contactAPI.deleteMessage(messageId);
        setShowDetailModal(false);
        fetchMessages();
        fetchStats();
        alert('Message deleted successfully!');
      } catch (error) {
        console.error('Error deleting message:', error);
        alert('Failed to delete message');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <MessagesContainer>
      <Header>
        <Title>Contact Messages</Title>
      </Header>

      {stats && (
        <StatsGrid>
          <StatCard theme={theme}>
            <StatIcon color="#1976d2">
              <MessageSquare size={24} />
            </StatIcon>
            <StatContent>
              <StatLabel theme={theme}>Total Messages</StatLabel>
              <StatValue theme={theme}>{stats.total}</StatValue>
            </StatContent>
          </StatCard>

          <StatCard theme={theme}>
            <StatIcon color="#2196f3">
              <Mail size={24} />
            </StatIcon>
            <StatContent>
              <StatLabel theme={theme}>New Messages</StatLabel>
              <StatValue theme={theme}>{stats.new}</StatValue>
            </StatContent>
          </StatCard>

          <StatCard theme={theme}>
            <StatIcon color="#ff9800">
              <Eye size={24} />
            </StatIcon>
            <StatContent>
              <StatLabel theme={theme}>Read</StatLabel>
              <StatValue theme={theme}>{stats.read}</StatValue>
            </StatContent>
          </StatCard>

          <StatCard theme={theme}>
            <StatIcon color="#4caf50">
              <CheckCircle size={24} />
            </StatIcon>
            <StatContent>
              <StatLabel theme={theme}>Replied</StatLabel>
              <StatValue theme={theme}>{stats.replied}</StatValue>
            </StatContent>
          </StatCard>
        </StatsGrid>
      )}

      <Controls>
        <SearchBox theme={theme}>
          <form onSubmit={handleSearch}>
            <input
              type="text"
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Search size={20} />
          </form>
        </SearchBox>

        <FilterButtons>
          <FilterButton
            active={statusFilter === 'all'}
            onClick={() => setStatusFilter('all')}
            theme={theme}
          >
            All
          </FilterButton>
          <FilterButton
            active={statusFilter === 'new'}
            onClick={() => setStatusFilter('new')}
            theme={theme}
          >
            New
          </FilterButton>
          <FilterButton
            active={statusFilter === 'read'}
            onClick={() => setStatusFilter('read')}
            theme={theme}
          >
            Read
          </FilterButton>
          <FilterButton
            active={statusFilter === 'replied'}
            onClick={() => setStatusFilter('replied')}
            theme={theme}
          >
            Replied
          </FilterButton>
          <FilterButton
            active={statusFilter === 'archived'}
            onClick={() => setStatusFilter('archived')}
            theme={theme}
          >
            Archived
          </FilterButton>
        </FilterButtons>
      </Controls>

      {loading ? (
        <Card style={{ padding: '3rem', textAlign: 'center' }}>Loading messages...</Card>
      ) : messages.length === 0 ? (
        <Card style={{ padding: '3rem', textAlign: 'center', color: theme.colors.text.secondary }}>
          No messages found.
        </Card>
      ) : (
        <MessagesTable theme={theme}>
          <TableHead theme={theme}>
            <tr>
              <TableHeader theme={theme}>Name</TableHeader>
              <TableHeader theme={theme}>Email</TableHeader>
              <TableHeader theme={theme}>Subject</TableHeader>
              <TableHeader theme={theme}>Status</TableHeader>
              <TableHeader theme={theme}>Date</TableHeader>
              <TableHeader theme={theme}>Actions</TableHeader>
            </tr>
          </TableHead>
          <tbody>
            {messages.map((message) => (
              <TableRow key={message.id} theme={theme}>
                <TableCell theme={theme}>{message.name}</TableCell>
                <TableCell theme={theme}>{message.email}</TableCell>
                <TableCell theme={theme}>{message.subject}</TableCell>
                <TableCell theme={theme}>
                  <StatusBadge status={message.status} theme={theme}>
                    {message.status}
                  </StatusBadge>
                </TableCell>
                <TableCell theme={theme}>{formatDate(message.created_at)}</TableCell>
                <TableCell theme={theme}>
                  <ActionButtons>
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => viewMessage(message)}
                    >
                      <Eye size={16} /> View
                    </Button>
                  </ActionButtons>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </MessagesTable>
      )}

      {selectedMessage && (
        <Modal
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          title="Message Details"
          size="large"
        >
          <MessageDetail theme={theme}>
            <DetailRow>
              <DetailLabel theme={theme}>From</DetailLabel>
              <DetailValue theme={theme}>
                <strong>{selectedMessage.name}</strong><br />
                Email: <a href={`mailto:${selectedMessage.email}`}>{selectedMessage.email}</a><br />
                {selectedMessage.phone && `Phone: ${selectedMessage.phone}`}
              </DetailValue>
            </DetailRow>

            <DetailRow>
              <DetailLabel theme={theme}>Subject</DetailLabel>
              <DetailValue theme={theme}>{selectedMessage.subject}</DetailValue>
            </DetailRow>

            <DetailRow>
              <DetailLabel theme={theme}>Message</DetailLabel>
              <DetailValue theme={theme}>{selectedMessage.message}</DetailValue>
            </DetailRow>

            <DetailRow>
              <DetailLabel theme={theme}>Status</DetailLabel>
              <DetailValue theme={theme}>
                <StatusBadge status={selectedMessage.status} theme={theme}>
                  {selectedMessage.status}
                </StatusBadge>
              </DetailValue>
            </DetailRow>

            <DetailRow>
              <DetailLabel theme={theme}>Received</DetailLabel>
              <DetailValue theme={theme}>{formatDate(selectedMessage.created_at)}</DetailValue>
            </DetailRow>

            <DetailRow>
              <DetailLabel theme={theme}>Admin Notes</DetailLabel>
              <TextArea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add notes about this message..."
                theme={theme}
              />
            </DetailRow>

            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <Button
                variant="primary"
                size="small"
                onClick={() => updateMessageStatus('replied')}
              >
                <CheckCircle size={16} /> Mark as Replied
              </Button>
              <Button
                variant="secondary"
                size="small"
                onClick={() => updateMessageStatus('archived')}
              >
                <Archive size={16} /> Archive
              </Button>
              <Button
                variant="outline"
                size="small"
                onClick={() => deleteMessage(selectedMessage.id)}
                style={{ color: theme.colors.error }}
              >
                <Trash2 size={16} /> Delete
              </Button>
            </div>
          </MessageDetail>
        </Modal>
      )}
    </MessagesContainer>
  );
};

export default ContactMessages;























